import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = ["commune", "rue", "numero", "quartier", "pariteHelp", "pariteText", "agentCard"]
  static values = { url: String }

  connect() {
    if (!this.hasUrlValue) {
      this.urlValue = "/quartiers_rues.json"
    }
    this.quartiersData = null
    this.ruesIndex = new Map()
    this.communeSelect = null
    this.rueSelect = null
    this.loadData()
  }

  disconnect() {
    this.destroyTomSelect()
    this.quartiersData = null
    this.ruesIndex.clear()
  }

  destroyTomSelect() {
    if (this.communeSelect) {
      this.communeSelect.destroy()
      this.communeSelect = null
    }
    if (this.rueSelect) {
      this.rueSelect.destroy()
      this.rueSelect = null
    }
  }

  async loadData() {
    try {
      const resp = await fetch(this.urlValue)
      this.quartiersData = await resp.json()
      this.populateCommunes()
      this.initTomSelect()
    } catch (error) {
      console.error("Erreur chargement données quartiers:", error)
    }
  }

  initTomSelect() {
    // Initialize Tom Select for commune
    this.communeSelect = new TomSelect(this.communeTarget, {
      placeholder: "Choisir une commune...",
      allowEmptyOption: true,
      onChange: (value) => this.onCommuneChange(value)
    })

    // Initialize Tom Select for rue (disabled initially)
    this.rueSelect = new TomSelect(this.rueTarget, {
      placeholder: "Choisir une rue...",
      allowEmptyOption: true,
      onChange: (value) => this.onRueChange(value)
    })
    this.rueSelect.disable()
  }

  populateCommunes() {
    if (!this.quartiersData?.communes) return

    const select = this.communeTarget
    select.innerHTML = '<option value="">Choisir une commune...</option>'

    Object.keys(this.quartiersData.communes).sort().forEach(commune => {
      const option = document.createElement("option")
      option.value = commune
      option.textContent = commune
      select.appendChild(option)
    })
  }

  onCommuneChange(commune) {
    this.resetRue()
    this.resetNumero()
    this.resetAgent()

    if (!commune) {
      this.rueSelect.disable()
      return
    }

    this.buildRuesIndex(commune)
    this.populateRues()
    this.rueSelect.enable()
  }

  buildRuesIndex(commune) {
    this.ruesIndex.clear()
    const communeData = this.quartiersData.communes[commune]
    if (!communeData?.quartiers) return

    communeData.quartiers.forEach(quartier => {
      quartier.rues.forEach(rue => {
        const rueName = typeof rue === "string" ? rue : rue.nom
        const rueData = typeof rue === "string" ? null : rue

        if (!this.ruesIndex.has(rueName)) {
          this.ruesIndex.set(rueName, [])
        }
        this.ruesIndex.get(rueName).push({ quartier, rueData })
      })
    })
  }

  populateRues() {
    // Clear existing options
    this.rueSelect.clear()
    this.rueSelect.clearOptions()

    // Add empty option
    this.rueSelect.addOption({ value: "", text: "Choisir une rue..." })

    const ruesSorted = Array.from(this.ruesIndex.keys()).sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base" })
    )

    ruesSorted.forEach(rue => {
      this.rueSelect.addOption({ value: rue, text: rue })
    })

    this.rueSelect.refreshOptions(false)
  }

  onRueChange(rueName) {
    this.resetNumero()
    this.resetAgent()

    if (!rueName) {
      this.numeroTarget.disabled = true
      return
    }

    const rueEntries = this.ruesIndex.get(rueName)
    if (!rueEntries || rueEntries.length === 0) return

    // Check if any entry has number restrictions
    const hasRestrictions = rueEntries.some(e => e.rueData?.numeros)

    if (hasRestrictions) {
      this.numeroTarget.disabled = false
      this.showPariteInfo(rueEntries)
    } else {
      // No number restrictions - show the quartier directly
      this.numeroTarget.disabled = true
      this.displayQuartier(rueEntries[0].quartier)
    }
  }

  showPariteInfo(rueEntries) {
    const restrictions = []
    rueEntries.forEach(entry => {
      if (entry.rueData?.numeros) {
        entry.rueData.numeros.forEach(r => {
          restrictions.push(`${r.type} de ${r.de} à ${r.a} (${entry.quartier.denomination})`)
        })
      }
    })

    if (restrictions.length > 0 && this.hasPariteHelpTarget) {
      this.pariteTextTarget.textContent = restrictions.join(" | ")
      this.pariteHelpTarget.style.display = "block"
    }
  }

  validateNumero() {
    const rueName = this.rueSelect.getValue()
    const numero = parseInt(this.numeroTarget.value, 10)

    if (!rueName || isNaN(numero)) {
      this.resetAgent()
      return
    }

    const rueEntries = this.ruesIndex.get(rueName)
    if (!rueEntries) return

    // Find matching quartier based on number
    const matchingEntry = this.findMatchingQuartier(rueEntries, numero)

    if (matchingEntry) {
      this.numeroTarget.classList.remove("is-invalid")
      this.numeroTarget.classList.add("is-valid")
      this.displayQuartier(matchingEntry.quartier)
    } else {
      this.numeroTarget.classList.remove("is-valid")
      this.numeroTarget.classList.add("is-invalid")
      this.resetAgent()
    }
  }

  findMatchingQuartier(rueEntries, numero) {
    const isOdd = numero % 2 !== 0

    for (const entry of rueEntries) {
      // If no restrictions, this entry covers all numbers
      if (!entry.rueData?.numeros) {
        return entry
      }

      // Check each restriction
      for (const r of entry.rueData.numeros) {
        const matchesParity =
          (r.type === "Impair" && isOdd) ||
          (r.type === "Pair" && !isOdd)

        const inRange = numero >= parseInt(r.de, 10) && numero <= parseInt(r.a, 10)

        if (matchesParity && inRange) {
          return entry
        }
      }
    }

    return null
  }

  displayQuartier(quartier) {
    if (this.hasQuartierTarget) {
      this.quartierTarget.value = quartier.denomination
    }

    if (this.hasAgentCardTarget) {
      const agentName = quartier.agent || "Non assigné"
      this.agentCardTarget.innerHTML = `
        <div class="card border-primary">
          <div class="card-header bg-primary text-white">
            <i class="fas fa-user-shield me-2"></i>Inspecteur de quartier
          </div>
          <div class="card-body">
            <h5 class="card-title">${agentName}</h5>
          </div>
        </div>
      `
      this.agentCardTarget.style.display = "block"
    }
  }

  resetRue() {
    if (this.rueSelect) {
      this.rueSelect.clear()
      this.rueSelect.clearOptions()
      this.rueSelect.addOption({ value: "", text: "Choisir une rue..." })
      this.rueSelect.disable()
    }
  }

  resetNumero() {
    this.numeroTarget.value = ""
    this.numeroTarget.disabled = true
    this.numeroTarget.classList.remove("is-valid", "is-invalid")
    if (this.hasPariteHelpTarget) {
      this.pariteHelpTarget.style.display = "none"
    }
  }

  resetAgent() {
    if (this.hasQuartierTarget) {
      this.quartierTarget.value = ""
    }
    if (this.hasAgentCardTarget) {
      this.agentCardTarget.style.display = "none"
      this.agentCardTarget.innerHTML = ""
    }
  }

  resetForm() {
    if (this.communeSelect) {
      this.communeSelect.clear()
    }
    this.resetRue()
    this.resetNumero()
    this.resetAgent()
    this.ruesIndex.clear()
  }
}
